import { useMemo, useState } from 'react';
import { GlassModal } from '@/components/ui/glass-modal';
import { Button } from '@/components/ui/button';
import { Loader2, AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react';
import { UserItem } from '@/types/userItem';
import { cn } from '@/lib/utils';

interface BulkListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: UserItem[];
  isProcessing: boolean;
  /** Fired when the user wants to price unpriced items one at a time. */
  onNeedsPricing: (unpriced: UserItem[]) => void;
  /** Fired when the user wants to list everyone as-is (offers-only for unpriced when enabled). */
  onConfirm: (enableOffersForUnpriced: boolean) => Promise<void>;
}

export function BulkListDialog({
  open,
  onOpenChange,
  items,
  isProcessing,
  onNeedsPricing,
  onConfirm,
}: BulkListDialogProps) {
  const [enableOffersForPriceless, setEnableOffersForPriceless] = useState(false);

  const analysis = useMemo(() => {
    const withPrice = items.filter((i) => i.asking_price_cents && i.asking_price_cents > 0);
    const withoutPrice = items.filter((i) => !i.asking_price_cents || i.asking_price_cents === 0);
    return {
      total: items.length,
      withPrice,
      withoutPrice,
      readyToList: withPrice.length,
      needsPrice: withoutPrice.length,
    };
  }, [items]);

  const handlePriceEach = () => {
    onNeedsPricing(analysis.withoutPrice);
  };

  const handleListNow = () => {
    onConfirm(enableOffersForPriceless);
  };

  // Primary CTA logic:
  //  - Everyone priced → "List N" runs immediately.
  //  - Some unpriced + "Accept offers only" on → "List N" (unpriced go up as offers).
  //  - Some unpriced + toggle off → "Price N items" hands off to the queue.
  const needsPricing = analysis.needsPrice > 0 && !enableOffersForPriceless;

  return (
    <GlassModal
      open={open}
      onOpenChange={onOpenChange}
      title="List Items for Sale"
      description="Review items before listing them on the marketplace."
      mobileMaxHeightDvh={92}
    >
      <div className="mb-2">
        <h2 className="text-base font-semibold">List Items for Sale</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Review items before listing them on the marketplace.
        </p>
      </div>

      <div className="space-y-4 py-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 p-3 rounded-lg bg-success/10 border border-success/20">
            <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0" />
            <div>
              <div className="text-sm font-medium">{analysis.readyToList} ready</div>
              <div className="text-xs text-muted-foreground">Have prices set</div>
            </div>
          </div>
          {analysis.needsPrice > 0 && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-caution/10 border border-caution/20">
              <AlertCircle className="w-5 h-5 text-caution flex-shrink-0" />
              <div>
                <div className="text-sm font-medium">{analysis.needsPrice} need price</div>
                <div className="text-xs text-muted-foreground">Price each or accept offers</div>
              </div>
            </div>
          )}
        </div>

        {analysis.needsPrice > 0 && (
          <div
            className={cn(
              'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
              enableOffersForPriceless
                ? 'bg-primary/10 border-primary/30'
                : 'bg-muted/30 border-border hover:bg-muted/50',
            )}
            onClick={() => setEnableOffersForPriceless(!enableOffersForPriceless)}
          >
            <div
              className={cn(
                'w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
                enableOffersForPriceless
                  ? 'bg-primary border-primary'
                  : 'border-muted-foreground/50',
              )}
            >
              {enableOffersForPriceless && (
                <CheckCircle2 className="w-3 h-3 text-primary-foreground" />
              )}
            </div>
            <div>
              <div className="text-sm font-medium">Accept offers only for the rest</div>
              <div className="text-xs text-muted-foreground">
                Skip pricing — list unpriced items open to offers
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={isProcessing}
          className="flex-1"
        >
          Cancel
        </Button>
        {needsPricing ? (
          <Button onClick={handlePriceEach} disabled={isProcessing} className="flex-1">
            Price {analysis.needsPrice} item{analysis.needsPrice !== 1 ? 's' : ''}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={handleListNow} disabled={isProcessing} className="flex-1">
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Listing...
              </>
            ) : (
              `List ${analysis.total} Item${analysis.total !== 1 ? 's' : ''}`
            )}
          </Button>
        )}
      </div>
    </GlassModal>
  );
}
